import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { SITE } from "@/lib/site";
import { Wordmark } from "@/components/common/ui";
import { LifestyleList, LifestyleMenu } from "./LifestyleMenu";
import { GuidesList, GuidesMenu } from "@/components/guides/GuidesMenu";
import { AccountButton } from "./AccountButton";

export const NAV = [
  { to: "/search", label: "Buy" },
  { to: "/bases", label: "Bases in 3D" },
  { to: "/military", label: "Military & PCS" },
  { to: "/sellers", label: "Sell" },
  { to: "/buyers", label: "Buyers" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const isOverlayRoute = (p) => ["/", "/military", "/bases"].includes(p) || p.startsWith("/bases/");

const NavItem = ({ n, light }) => (
  <NavLink
    to={n.to}
    data-testid={`nav-${n.to.slice(1)}`}
    className={({ isActive }) =>
      `text-[15px] transition-opacity hover:opacity-70 ${light ? "text-white" : "text-ink"} ${
        isActive ? "underline decoration-brass decoration-2 underline-offset-[10px]" : ""
      }`
    }
  >
    {n.label}
  </NavLink>
);

export const SiteHeader = () => {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const overlay = isOverlayRoute(pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const light = overlay && !scrolled;
  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 h-16 transition-[background-color,box-shadow] duration-300 ${
        light ? "bg-gradient-to-b from-black/55 to-transparent" : "border-b border-neutral-200 bg-white/95 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-full max-w-[1440px] items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="header-logo-link" aria-label="757 Realty home">
          <Wordmark light={light} />
        </Link>
        <nav className="hidden items-center gap-6 xl:flex" aria-label="Main">
          {NAV.slice(0, 1).map((n) => <NavItem key={n.to} n={n} light={light} />)}
          <LifestyleMenu light={light} />
          <GuidesMenu light={light} />
          {NAV.slice(1).map((n) => <NavItem key={n.to} n={n} light={light} />)}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href={SITE.phoneMainHref}
            data-testid="header-call-button"
            className={`hidden items-center gap-2 rounded-full px-4 py-2 text-[15px] font-medium transition-colors 2xl:inline-flex ${
              light ? "bg-white text-ink hover:bg-neutral-100" : "bg-ink text-white hover:bg-neutral-800"
            }`}
          >
            <Phone className="h-4 w-4" /> {SITE.phoneMain}
          </a>
          <a href={SITE.phoneMainHref} aria-label={`Call ${SITE.phoneMain}`} data-testid="header-call-icon"
            className={`hidden h-10 w-10 place-items-center rounded-full sm:grid 2xl:hidden ${light ? "bg-white text-ink" : "bg-ink text-white"}`}>
            <Phone className="h-4 w-4" />
          </a>
          <AccountButton light={light} />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className={`grid h-10 w-10 place-items-center rounded-full xl:hidden ${light ? "text-white" : "text-ink"}`}
                data-testid="mobile-menu-button"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] max-w-[88vw] overflow-y-auto overscroll-contain bg-white" data-testid="mobile-menu">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <Wordmark className="mb-8" />
              <nav className="flex flex-col gap-1">
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} data-testid={`mobile-nav-${n.to.slice(1)}`}
                    className="border-b border-neutral-100 py-3 text-lg text-ink">
                    {n.label}
                  </NavLink>
                ))}
                <p className="pt-4 text-lg text-ink">Lifestyle</p>
                <LifestyleList />
                <p className="pt-4 text-lg text-ink">Guides</p>
                <GuidesList />
              </nav>
              <a href={SITE.phoneMainHref} data-testid="mobile-menu-call"
                className="mt-8 flex items-center justify-center gap-2 rounded-full bg-ink py-3 text-white">
                <Phone className="h-4 w-4" /> Call {SITE.phoneMain}
              </a>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
